import React, { useState, useContext } from "react";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import { InitialPasswordContainerContext } from "src/components/InitialPasswordContainerContext";
import WellDone from "./WellDone";
import {
    AuthDescription,
    AuthForm,
    AuthInput,
    AuthPrimaryButton,
    AuthSurface,
    AuthTitle,
    ErrorLabel,
    LottieLoadingIndicator,
} from "src/components";

// Error constants
const InitialPasswordError = {
    EMPTY_PASSWORD: 1,
    EMPTY_CONFIRM_PASSWORD: 2,
    DIFF_PASS: 3,
    MIN_LENGTH: 4,
};

const ErrorStrings = {
    [InitialPasswordError.EMPTY_PASSWORD]: "* Please enter a password",
    [InitialPasswordError.EMPTY_CONFIRM_PASSWORD]: "* Please re-enter the same password",
    [InitialPasswordError.DIFF_PASS]: "* Please make sure passwords match",
    [InitialPasswordError.MIN_LENGTH]:
        "* Please enter a password at least eight characters long",
};

const InitialPassword = () => {
    const [isShowWellDone, setIsShowWellDone] = useState(false);

    if (isShowWellDone) return <WellDone />;

    return (
        <AuthSurface>
            <AuthTitle>Welcome to the Me Business App!</AuthTitle>
            <AuthDescription>To get started with your account, create your password. You&apos;ll use this to log in to the Business Portal and manage your account.</AuthDescription>
            <Password showWellDone={setIsShowWellDone} />
        </AuthSurface>
    );
};

const Password = ({ showWellDone }) => {
    const { shortTermToken } = useContext(InitialPasswordContainerContext);

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [inputErrors, setInputErrors] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const getErrorStrings = (...errs) =>
        inputErrors.filter((err) => errs.includes(err)).map((err) => ErrorStrings[err]);

    const passwordErrors = getErrorStrings(
        InitialPasswordError.EMPTY_PASSWORD,
        InitialPasswordError.MIN_LENGTH
    );
    const confirmErrors = getErrorStrings(
        InitialPasswordError.EMPTY_CONFIRM_PASSWORD,
        InitialPasswordError.DIFF_PASS
    );

    const submitInitialPassword = (e) => {
        e.preventDefault();
        let errorArray = [];
        setInputErrors([]);
        setErrorMessage("");

        if (password.trim() === "") {
            errorArray.push(InitialPasswordError.EMPTY_PASSWORD);
        } else if (password.length < 8) {
            errorArray.push(InitialPasswordError.MIN_LENGTH);
        } else if (confirmPassword.trim() === "") {
            errorArray.push(InitialPasswordError.EMPTY_CONFIRM_PASSWORD);
        } else if (password !== confirmPassword) {
            errorArray.push(InitialPasswordError.DIFF_PASS);
        }

        if (errorArray.length > 0) {
            setInputErrors(errorArray);
        } else {
            requestSubmitPassword();
        }
    };

    const requestSubmitPassword = async () => {
        try {
            setLoading(true);
            await axios.put(`${API_URL}/setupPassword/${shortTermToken}`, {
                password,
            });
            setLoading(false);
            showWellDone(true);
        } catch (error) {
            setErrorMessage(error.getApiErrorDetails().message);
            setLoading(false);
        }
    };

    return (
        <AuthForm onSubmit={submitInitialPassword}>
            {errorMessage && <ErrorLabel align="center">{errorMessage}</ErrorLabel>}
            <AuthInput placeholder="Enter password" type="password" onChange={event => setPassword(event.target.value)} />
            {passwordErrors.map(error => <ErrorLabel align="center" key={error}>{error}</ErrorLabel>)}
            <AuthInput placeholder="Re-enter password" type="password" onChange={event => setConfirmPassword(event.target.value)} />
            {confirmErrors.map(error => <ErrorLabel align="center" key={error}>{error}</ErrorLabel>)}
            {loading ? <LottieLoadingIndicator height="3rem" width="3rem" /> : <AuthPrimaryButton type="submit">Done</AuthPrimaryButton>}
        </AuthForm>
    );
};

export default InitialPassword;
